<template>
  <div class="max-w-screen min-h-screen w-full bg-background text-foreground">
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader
        :alert-order="['unlicensed', 'err', 'agentIncompatible', 'userLimitExceeded', 'serverUpdate']"
        :alert-triggers="{ err: () => Boolean(errorMessage) }"
      >
        <template #err>
          <div class="bg-primary px-4 py-4">
            {{ errorMessage }}
          </div>
        </template>

        <template #append>
          <div class="grid grid-cols-1 border-b border-white/5 lg:grid-cols-3" role="list">
            <div class="border-t border-white/5 px-4 py-6 sm:px-6 lg:px-8" role="listitem">
              <p class="text-sm font-medium leading-6 text-[#ccd1c4]">{{ t("msg.activeSessions") }}</p>
              <p class="mt-2 flex items-baseline gap-x-2">
                <span class="text-4xl font-semibold tracking-tight text-white">
                  {{ activeSessions }}
                </span>
              </p>
            </div>
            <div class="border-t border-white/5 px-4 py-6 sm:border-l sm:px-6 lg:px-8" role="listitem">
              <p class="text-sm font-medium leading-6 text-[#ccd1c4]">{{ t("msg.aclCount") }}</p>
              <p class="mt-2 flex items-baseline gap-x-2">
                <span class="text-4xl font-semibold tracking-tight text-white">{{ aclCount }}</span>
              </p>
            </div>
            <div class="border-t border-white/5 px-4 py-6 sm:px-6 lg:border-l lg:px-8" role="listitem">
              <p class="text-sm font-medium leading-6 text-[#ccd1c4]">{{ t("msg.userCount") }}</p>
              <p class="mt-2 flex items-baseline gap-x-2">
                <span class="text-4xl font-semibold tracking-tight text-white">{{ userCount }}</span>
              </p>
            </div>
          </div>
        </template>
      </AdminHeader>

      <main>
        <div class="mx-auto px-12 py-10">
          <div class="sm:flex sm:items-center">
            <div class="sm:flex-auto">
              <h1 class="text-base font-semibold text-white">{{ t("msg.recentActivity") }}</h1>
            </div>
          </div>
          <div class="mt-8 flow-root overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{{ t("msg.user") }}</TableHead>
                  <TableHead>{{ t("msg.date") }}</TableHead>
                  <TableHead>{{ t("msg.action") }}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow v-for="item in activityItems">
                  <TableCell>{{ globals.logEntryActor(item.Entry) }}</TableCell>
                  <TableCell>{{ DateTime.fromISO(item.Date).toRelative() }}</TableCell>
                  <TableCell>{{ t(globals.logEntryAction(item.Entry)) }}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { Bars3Icon } from "@heroicons/vue/20/solid";
import { DateTime } from "luxon";
import { useI18n } from "vue-i18n";

import globals from "../../globals";

import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import AdminHeader from "../../components/AdminHeader.vue";
import { useAdminStore } from "../../pinia/admin.ts";

const { t } = useI18n();

const adminStore = useAdminStore();

const search = ref("");

const errorMessage = ref("");

const activeSessions = ref(adminStore.user?.SessionCount);
const aclCount = ref(adminStore.user?.ACLCount);
const userCount = ref(adminStore.user?.UserCount);
const activityItems = ref(adminStore.user?.Activity);
const cloudMode = ref(adminStore.user?.Cloud);

const updateErrorMessage = (licensing) => {
  if (!cloudMode.value) {
    const expiryDate = DateTime.fromISO(licensing.Expires);
    const sixtyDaysFromNow = DateTime.now().plus({ days: 60 });

    if (expiryDate <= sixtyDaysFromNow) {
      errorMessage.value = t("err.licenseExpiresSoon");
    }
  }
};

const sidebarOpen = ref(false);

onMounted(() => {
  const fetchAndUpdate = async () => {
    try {
      await adminStore.fetchStatus({ force: true });
      activeSessions.value = adminStore.user.SessionCount;
      aclCount.value = adminStore.user.ACLCount;
      userCount.value = adminStore.user.UserCount;
      updateErrorMessage(adminStore.user.Licensing);
    } catch (err) {
      console.error("Failed to update admin status:", err);
    }
  };

  fetchAndUpdate();

  setInterval(fetchAndUpdate, 30 * 1000);
});
</script>
